// src/shared/role-periods.js - promotions and acting roles, the rules (v133.52).
//
// Matt, 6 Oct 2026: when someone is promoted (two store managers became area managers), their record
// should carry on and become the new role, not be replaced; and it must also allow a TEMPORARY step up,
// for example a senior store manager acting as area manager while the area manager is on holiday.
//
// One row per change in staff_role_periods (migration v231-0), never edited away:
//   - PERMANENT: the person's role changes from the start date. staff.role is updated at the same time,
//     so every list that reads staff.role (induction, reviews, training) follows it. The start date may
//     be today or in the past (to record when it really happened), never in the future: nothing applies
//     a future change automatically, so a future date would be a promise the app does not keep.
//   - ACTING: a temporary role between two dates. staff.role does NOT change, so the person stays in
//     their own store's lists. The acting role shows on their record while it runs and simply stops
//     on the end date; nobody has to remember to undo it.
// Head Office can end an acting period early, or mark a row as a mistake. Neither deletes the row.
//
// Pure: no I/O. Imported by the Worker route (src/worker/routes/staff-roles.js) and the admin bundle
// (src/staffRoles/index.js), so both apply the same rules.

export const ROLE_PERIODS_TABLE = 'staff_role_periods';
export const PERIOD_KINDS = ['permanent', 'acting'];

// The roles a person can be promoted to, or act up as. The values are the ones staff.role already holds.
export const PROMOTION_ROLES = [
  { value: 'area_manager', label: 'Area Manager' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'Store in-charge', label: 'Store in-charge' },
  { value: 'Head Office', label: 'Head Office' },
];

const LABELS = {
  area_manager: 'Area Manager', store_manager: 'Store Manager', 'store in-charge': 'Store in-charge',
  'head office': 'Head Office', head_office: 'Head Office', 'sales assistant': 'Sales Assistant',
};
export function roleLabel(role) {
  const r = String(role == null ? '' : role).trim();
  return LABELS[r.toLowerCase()] || LABELS[r] || r || 'No role';
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function isDate(s) {
  if (!DATE_RE.test(String(s || ''))) return false;
  const d = new Date(s + 'T00:00:00Z');
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
const MAX_ACTING_DAYS = 366;
function daysBetween(a, b) { return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000); }

// Checks a promotion request. Returns { error } or { row } (the fields the caller stores, before
// identity: the Worker adds staff_id, from_role, set_by_*). `today` is a Dubai YYYY-MM-DD.
export function checkPromotion(input, opts) {
  const o = opts || {};
  const today = o.today;
  const b = input || {};
  const kind = String(b.kind || '');
  if (PERIOD_KINDS.indexOf(kind) < 0) return { error: 'Choose permanent or acting.' };
  const role = String(b.role || '');
  if (!PROMOTION_ROLES.some((r) => r.value === role)) return { error: 'Choose a role.' };
  const startsOn = String(b.starts_on || '');
  if (!isDate(startsOn)) return { error: 'Choose a start date.' };
  const current = String(o.currentRole == null ? '' : o.currentRole).trim().toLowerCase();
  let endsOn = null;
  if (kind === 'permanent') {
    if (today && startsOn > today) return { error: 'A permanent change starts today or on a past date. Come back on the day to make it.' };
    if (current && current === role.toLowerCase()) return { error: 'They are already ' + roleLabel(role) + '.' };
  } else {
    endsOn = String(b.ends_on || '');
    if (!isDate(endsOn)) return { error: 'An acting role needs an end date.' };
    if (endsOn < startsOn) return { error: 'The end date is before the start date.' };
    if (today && endsOn < today) return { error: 'That acting period has already ended.' };
    if (daysBetween(startsOn, endsOn) > MAX_ACTING_DAYS) return { error: 'An acting role can last up to a year. Make it permanent if it is longer.' };
    if (current && current === role.toLowerCase()) return { error: 'They are already ' + roleLabel(role) + '.' };
  }
  // areas.id is TEXT ('1', '2', 'demo-1'), so the id is kept as a short string, never a number
  const areaId = b.area_id == null || String(b.area_id).trim() === '' ? null : String(b.area_id).trim();
  if (areaId != null && !/^[A-Za-z0-9_-]{1,40}$/.test(areaId)) return { error: 'Choose an area.' };
  if (role === 'area_manager' && areaId == null) return { error: 'Choose the area they will manage.' };
  const note = String(b.note == null ? '' : b.note).trim().slice(0, 500);
  return { row: { kind, role, starts_on: startsOn, ends_on: endsOn, area_id: areaId, note: note || null } };
}

// A row that counts: not marked as a mistake.
function live(p) { return p && !p.cancelled_at; }

// The acting period covering `date`, if any (the latest start wins if two overlap).
export function actingOn(periods, date) {
  const list = (Array.isArray(periods) ? periods : []).filter((p) => live(p) && p.kind === 'acting'
    && p.starts_on <= date && (p.ends_on == null || p.ends_on >= date));
  list.sort((a, b) => (a.starts_on < b.starts_on ? 1 : a.starts_on > b.starts_on ? -1 : (b.id || 0) - (a.id || 0)));
  return list[0] || null;
}

// What to show for a person on `date`: their own role, and an acting role on top while one runs.
// The lists (induction, reviews, training) keep using staff.role, which is the permanent role.
export function roleOn(staff, periods, date) {
  const base = staff && staff.role != null ? staff.role : '';
  const acting = actingOn(periods, date);
  return acting
    ? { role: acting.role, base, acting: true, until: acting.ends_on, areaId: acting.area_id == null ? null : acting.area_id }
    : { role: base, base, acting: false, until: null, areaId: null };
}

// Acting periods that have not started yet (shown so a planned holiday cover is visible in advance).
export function upcomingActing(periods, date) {
  return (Array.isArray(periods) ? periods : []).filter((p) => live(p) && p.kind === 'acting' && p.starts_on > date)
    .sort((a, b) => (a.starts_on < b.starts_on ? -1 : 1));
}

// Head Office ending an acting period early: the new end date is yesterday if it started before today
// (it stops at once), otherwise the row is a period that never ran and is marked as a mistake instead.
export function endEarly(period, today) {
  if (!period || period.kind !== 'acting' || !live(period)) return { error: 'Only a running or planned acting role can be ended.' };
  if (period.ends_on != null && period.ends_on < today) return { error: 'That acting role has already ended.' };
  if (period.starts_on >= today) return { cancel: true };
  const y = new Date(Date.parse(today + 'T00:00:00Z') - 86400000).toISOString().slice(0, 10);
  return { ends_on: y };
}

function fmt(d) {
  if (!isDate(d)) return '';
  return new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// One line of history, newest first in the list. `areaName` turns an area id into a name.
export function describePeriod(p, areaName) {
  if (!p) return '';
  const area = p.area_id != null && typeof areaName === 'function' ? areaName(p.area_id) : '';
  const where = area ? ' (' + area + ')' : '';
  let text;
  if (p.kind === 'permanent') {
    text = (p.from_role ? roleLabel(p.from_role) + ' to ' : '') + roleLabel(p.role) + where + ', from ' + fmt(p.starts_on);
  } else {
    text = 'Acting ' + roleLabel(p.role) + where + ', ' + fmt(p.starts_on) + ' to ' + fmt(p.ends_on);
  }
  if (p.cancelled_at) text += ' (marked as a mistake)';
  return text;
}
